import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Settings, Building, Shield, Palette, Users, BarChart3, Download, Eye, Info, Plus, Edit2, Trash2, CheckCircle } from 'lucide-react';
import StickyActionBar from '../../components/ui/StickyActionBar';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import CompanyControlsTab from './components/CompanyControlsTab';
import FieldGovernanceTab, { type FieldGovernanceTabRef } from './components/FieldGovernanceTab';
import BrandingTab, { type BrandingTabRef } from './components/BrandingTab';
import ProfileDirectoryTab from './components/ProfileDirectoryTab';
import AnalyticsAuditTab from './components/AnalyticsAuditTab';
import ApprovalTab from './components/ApprovalTab';
import AddPersonaModal from './components/AddPersonaModal';
import EditPersonaModal from './components/EditPersonaModal';
import DeletePersonaModal from './components/DeletePersonaModal';
import {
  getPersonas,
  createPersona,
  updatePersona,
  deletePersona,
  type ProfilePersonaView,
  type ProfilePersonaInput,
} from '../../api/PublicProfile/publicProfileApi';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { todayLocalDateParam } from '@/utils/apiDate';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

const VALID_TABS = ['company-controls', 'field-governance', 'branding', 'profile-directory', 'approvals', 'analytics'];

const HRAdminPublicProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'company-controls';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });

  // Refs for tabs that have saveable state
  const brandingTabRef = useRef<BrandingTabRef>(null);
  const fieldGovernanceTabRef = useRef<FieldGovernanceTabRef>(null);

  const [personas, setPersonas] = useState<ProfilePersonaView[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [personaToEdit, setPersonaToEdit] = useState<ProfilePersonaView | null>(null);
  const [personaToDelete, setPersonaToDelete] = useState<ProfilePersonaView | null>(null);

  const tabItems = [
    { id: 'company-controls', label: t('Company Controls'), icon: Building },
    { id: 'field-governance', label: t('Field Governance'), icon: Shield },
    { id: 'branding', label: t('Branding & Templates'), icon: Palette },
    { id: 'profile-directory', label: t('Profile Directory'), icon: Users },
    { id: 'approvals', label: t('Approvals'), icon: CheckCircle },
    { id: 'analytics', label: t('Analytics & Audit'), icon: BarChart3 }
  ];

  // Fetch personas on mount
  useEffect(() => {
    fetchPersonas();
  }, []);

  const fetchPersonas = async () => {
    setIsLoading(true);
    try {
      const result = await getPersonas();
      if (result.status === 200 && result.payload) {
        setPersonas(result.payload);
        // Select the default persona or the first one
        const defaultPersona = result.payload.find(p => p.isDefault) || result.payload[0];
        if (defaultPersona) {
          setSelectedPersonaId(defaultPersona.id);
        }
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to load personas'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to load personas'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);

    try {
      const savePromises: Promise<boolean>[] = [];

      // Save branding tab if it has changes
      if (brandingTabRef.current?.hasChanges()) {
        savePromises.push(brandingTabRef.current.save());
      }

      // Save field governance tab if it has changes
      if (fieldGovernanceTabRef.current?.hasChanges()) {
        savePromises.push(fieldGovernanceTabRef.current.save());
      }

      if (savePromises.length > 0) {
        const results = await Promise.all(savePromises);
        const allSuccessful = results.every(r => r);

        if (allSuccessful) {
          setHasUnsavedChanges(false);
          setSnackbar({ open: true, message: t('Settings saved successfully!'), severity: 'success' });
        } else {
          setSnackbar({ open: true, message: t('Some settings failed to save'), severity: 'error' });
        }
      } else {
        setHasUnsavedChanges(false);
        setSnackbar({ open: true, message: t('Settings saved successfully!'), severity: 'success' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({ open: true, message: t('Failed to save settings'), severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm(t('Are you sure you want to reset all settings?'))) {
      setHasUnsavedChanges(false);
      window.location?.reload();
    }
  };

  const handleExportSettings = () => {
    const settings = {
      companyControls: 'Current company control settings',
      fieldGovernance: 'Current field governance settings',
      branding: 'Current branding settings',
      security: 'Current security settings'
    };

    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `profile-settings-${todayLocalDateParam()}.json`;

    const linkElement = document.createElement('a');
    linkElement?.setAttribute('href', dataUri);
    linkElement?.setAttribute('download', exportFileDefaultName);
    linkElement?.click();
  };

  const handlePreview = () => {
    alert(t('Preview requires a valid profile slug. Create a profile first and use its public URL.'));
  };

  const handleAddPersona = async (input: ProfilePersonaInput) => {
    try {
      const result = await createPersona(input);
      if (result.status === 200 && result.payload) {
        setPersonas(prev => [...prev, result.payload!]);
        setSelectedPersonaId(result.payload.id);
        setSnackbar({ open: true, message: t('Persona created successfully'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to create persona'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to create persona'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleEditPersona = async (personaId: number, input: ProfilePersonaInput) => {
    try {
      const result = await updatePersona(personaId, input);
      if (result.status === 200 && result.payload) {
        setPersonas(prev => prev.map(p => p.id === personaId ? result.payload! : p));
        setSnackbar({ open: true, message: t('Persona updated successfully'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to update persona'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to update persona'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleDeletePersona = async (personaId: number) => {
    try {
      const result = await deletePersona(personaId);
      if (result.status === 200) {
        setPersonas(prev => prev.filter(p => p.id !== personaId));
        if (selectedPersonaId === personaId) {
          const remaining = personas.filter(p => p.id !== personaId);
          setSelectedPersonaId(remaining[0]?.id || null);
        }
        setSnackbar({ open: true, message: t('Persona deleted successfully'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to delete persona'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to delete persona'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleOpenEditModal = () => {
    const persona = personas.find(p => p.id === selectedPersonaId);
    setPersonaToEdit(persona || null);
    setShowEditModal(true);
  };

  const handleOpenDeleteModal = () => {
    const persona = personas.find(p => p.id === selectedPersonaId);
    setPersonaToDelete(persona || null);
    setShowDeleteModal(true);
  };

  // Render all tabs but only show active one - keeps refs valid for saving
  const renderTabContent = () => {
    const personaIdStr = selectedPersonaId?.toString() || '';
    return (
      <>
        <div className={activeTab === 'company-controls' ? '' : 'hidden'}>
          <CompanyControlsTab selectedPersonaId={personaIdStr} onSettingsChange={() => setHasUnsavedChanges(true)} />
        </div>
        <div className={activeTab === 'field-governance' ? '' : 'hidden'}>
          <FieldGovernanceTab ref={fieldGovernanceTabRef} selectedPersonaId={personaIdStr} onSettingsChange={() => setHasUnsavedChanges(true)} />
        </div>
        <div className={activeTab === 'branding' ? '' : 'hidden'}>
          <BrandingTab ref={brandingTabRef} selectedPersonaId={personaIdStr} onSettingsChange={() => setHasUnsavedChanges(true)} />
        </div>
        <div className={activeTab === 'profile-directory' ? '' : 'hidden'}>
          <ProfileDirectoryTab selectedPersonaId={personaIdStr} onSettingsChange={() => setHasUnsavedChanges(true)} />
        </div>
        <div className={activeTab === 'approvals' ? '' : 'hidden'}>
          <ApprovalTab selectedPersonaId={personaIdStr} onSettingsChange={() => setHasUnsavedChanges(true)} />
        </div>
        <div className={activeTab === 'analytics' ? '' : 'hidden'}>
          <AnalyticsAuditTab selectedPersonaId={personaIdStr} onSettingsChange={() => setHasUnsavedChanges(true)} />
        </div>
      </>
    );
  };

  const selectedPersona = personas.find(p => p.id === selectedPersonaId);

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-8 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header Section */}
          <Card variant="elevated" className="mb-6">
            <CardContent>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Settings className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">{t('HR Admin - Public Profile Settings')}</h1>
                    <p className="text-muted-foreground mt-1">{t('Manage company-wide public profile policies, governance, and security')}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    iconName="Download"
                    onClick={handleExportSettings}
                    className="min-h-[44px]"
                  >
                    {t('Export')}
                  </Button>

                  <Button
                    variant="outline"
                    iconName="Eye"
                    onClick={handlePreview}
                    className="min-h-[44px]"
                  >
                    {t('Preview Profile')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {isLoading && (
            <Card variant="elevated" className="mb-6">
              <CardContent className="p-12 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="ms-3 text-muted-foreground">{t('Loading personas...')}</span>
              </CardContent>
            </Card>
          )}

          {/* Public Profile Persona Section */}
          {!isLoading && (
            <Card variant="elevated" className="mb-6">
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 me-2 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">{t('Public Profile Persona')}</h2>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  {t('Select a persona to manage its settings across all tabs. Each persona can have unique configurations for company controls, field governance, branding, profile directory, and analytics.')}
                </p>

                <div className="flex flex-wrap items-end gap-3">
                  {personas.length > 0 ? (
                    <div className="flex-1 min-w-[250px]">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {t('Selected Persona')}
                      </label>
                      <select
                        value={selectedPersonaId || ''}
                        onChange={(e) => setSelectedPersonaId(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      >
                        {personas.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} {p.isDefault ? `(${t('Default')})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-[250px] p-3 bg-warning/10 border border-warning/20 rounded-lg">
                      <p className="text-sm text-warning">
                        {t('No personas configured yet. Create a persona to manage per-persona settings.')}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      iconName="Plus"
                      onClick={() => setShowAddModal(true)}
                      className="min-h-[44px]"
                    >
                      {t('Add Persona')}
                    </Button>

                    <Button
                      variant="outline"
                      iconName="Edit2"
                      onClick={handleOpenEditModal}
                      disabled={!selectedPersonaId}
                      className="min-h-[44px]"
                    >
                      {t('Edit')}
                    </Button>

                    <Button
                      variant="destructive"
                      iconName="Trash2"
                      onClick={handleOpenDeleteModal}
                      disabled={!selectedPersonaId || personas.length <= 1}
                      className="min-h-[44px]"
                    >
                      {t('Delete')}
                    </Button>
                  </div>
                </div>

                {selectedPersona && (
                  <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-4 h-4 text-primary mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-primary">
                          {selectedPersona.name}
                        </p>
                        <p className="text-sm text-primary mt-1">
                          {selectedPersona.description || t('No description')}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedPersona.isDefault && (
                            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                              {t('Default')}
                            </span>
                          )}
                          {selectedPersona.requiresApproval && (
                            <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs rounded-full">
                              {t('Requires Approval')}
                            </span>
                          )}
                          {selectedPersona.allowSlugRegeneration && (
                            <span className="px-2 py-0.5 bg-success/20 text-success text-xs rounded-full">
                              {t('Can Regenerate Link')}
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                            {selectedPersona.profileCount} {t('profiles')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tab Navigation */}
          {!isLoading && (
            <Card variant="elevated" className="mb-6">
              <div className="border-b border-border">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="flex flex-col sm:flex-row w-full bg-transparent p-0 rounded-none overflow-x-auto">
                    {tabItems.map((tab) => {
                      const IconComponent = tab.icon;
                      return (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="min-h-[44px] gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-4 text-sm font-medium whitespace-nowrap text-muted-foreground hover:text-foreground hover:border-border"
                        >
                          <IconComponent className="w-4 h-4" />
                          {tab.label}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
              </div>
            </Card>
          )}

          {/* Tab Content */}
          {!isLoading && (
            <div>
              {renderTabContent()}
            </div>
          )}

          {/* Sticky Action Bar */}
          {!isLoading && (
            <StickyActionBar
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              onReset={handleResetSettings}
              onSave={handleSaveSettings}
              t={t}
            />
          )}
        </div>
      </div>

      {/* Snackbar */}
      {snackbar.open && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 ${
          snackbar.severity === 'success' ? 'bg-success text-success-foreground' :
          snackbar.severity === 'error' ? 'bg-destructive text-destructive-foreground' :
          'bg-primary text-primary-foreground'
        }`}>
          <span>{snackbar.message}</span>
          <button onClick={() => setSnackbar(prev => ({ ...prev, open: false }))} className="ms-2 opacity-80 hover:opacity-100" aria-label={t('Close notification')}>
            &times;
          </button>
        </div>
      )}

      {/* Modals */}
      <AddPersonaModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddPersona}
      />

      <EditPersonaModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onEdit={handleEditPersona}
        persona={personaToEdit}
      />

      <DeletePersonaModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeletePersona}
        persona={personaToDelete}
      />
    </div>
  );
};

export default HRAdminPublicProfileSettings;
